// VALM CUSTOM: Utah fulfillment schedule, local cutoff display, and live lube countdown.
(() => {
  const UTAH_ZONE = 'America/Denver';
  const DAY = 86400000;
  const utahFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: UTAH_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  });

  function utahParts(date) {
    return Object.fromEntries(utahFormatter.formatToParts(date)
      .filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  }

  function utahInstant(year, month, day, hour) {
    const wallTime = Date.UTC(year, month - 1, day, hour);
    let timestamp = wallTime;
    for (let attempt = 0; attempt < 3; attempt++) {
      const parts = utahParts(new Date(timestamp));
      const formatted = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
      const difference = wallTime - formatted;
      timestamp += difference;
      if (difference === 0) break;
    }
    return timestamp;
  }

  function nextDispatch(now, weekdayCutoff, saturdayCutoff) {
    const today = utahParts(new Date(now));
    const calendarDate = Date.UTC(today.year, today.month - 1, today.day);
    for (let offset = 0; offset < 8; offset++) {
      const day = new Date(calendarDate + offset * DAY);
      if (day.getUTCDay() === 0) continue;
      const hour = day.getUTCDay() === 6 ? saturdayCutoff : weekdayCutoff;
      const cutoff = utahInstant(day.getUTCFullYear(), day.getUTCMonth() + 1, day.getUTCDate(), hour);
      if (cutoff > now) return { cutoff, sameUtahDay: offset === 0 };
    }
  }

  function customerZone() {
    try {
      const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!zone) return null;
      new Intl.DateTimeFormat('en-US', { timeZone: zone }).format();
      return zone;
    } catch {
      return null;
    }
  }

  function localDate(timestamp, zone, options) {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
      ...options, timeZone: zone || 'UTC'
    }).format(new Date(timestamp - (zone ? 0 : 8 * 3600000)));
  }

  class ValmProductShipping extends HTMLElement {
    static get observedAttributes() {
      return ['data-mode', 'data-available', 'data-weekday-cutoff', 'data-saturday-cutoff'];
    }

    attributeChangedCallback() {
      if (this.isConnected) queueMicrotask(() => {
        if (this.isConnected) this.connectedCallback();
      });
    }

    connectedCallback() {
      this.stop();
      if (this.dataset.mode !== 'lube' || this.dataset.available !== 'true') return;
      const translations = this.querySelector('[data-shipping-translations]');
      if (!translations) return;
      this.translations = JSON.parse(translations.textContent);
      this.zone = customerZone();
      this.lead = this.querySelector('[data-shipping-lead]');
      this.counter = this.querySelector('[data-shipping-countdown]');
      this.tail = this.querySelector('[data-shipping-tail]');
      this.detail = this.querySelector('[data-shipping-detail]');
      this.update = this.update.bind(this);
      this.onVisibility = () => {
        this.stopTimer();
        if (!document.hidden) {
          this.update();
          this.timer = setInterval(this.update, 1000);
        }
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onVisibility();
    }

    disconnectedCallback() {
      this.stop();
    }

    stopTimer() {
      clearInterval(this.timer);
      this.timer = null;
    }

    stop() {
      this.stopTimer();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    update() {
      const now = Date.now();
      const weekdayCutoff = Number(this.dataset.weekdayCutoff ?? 16);
      const saturdayCutoff = Number(this.dataset.saturdayCutoff ?? 10);
      const dispatch = nextDispatch(now, weekdayCutoff, saturdayCutoff);
      if (!dispatch) return;
      const t = this.translations;
      const dayOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
      const sameLocalDay = localDate(now, this.zone, dayOptions) === localDate(dispatch.cutoff, this.zone, dayOptions);
      const date = localDate(dispatch.cutoff, this.zone, { weekday: 'long', month: 'short', day: 'numeric' });
      const timeOptions = { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' };
      let time = localDate(dispatch.cutoff, this.zone, timeOptions);
      if (!this.zone) time = time.replace(/\s(?:UTC|GMT)$/, '') + ' PST';

      if (dispatch.sameUtahDay) {
        const totalSeconds = Math.max(1, Math.ceil((dispatch.cutoff - now) / 1000));
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor(totalSeconds % 3600 / 60);
        const seconds = totalSeconds % 60;
        const remaining = hours ? `${hours}${t.hours} ${minutes}${t.minutes}`
          : minutes ? `${minutes}${t.minutes}` : `${seconds}${t.seconds}`;
        const template = (sameLocalDay ? t.countdown : t.countdown_date).replace('__DATE__', date);
        const [lead, tail] = template.split('__COUNTDOWN__');
        this.lead.textContent = lead;
        this.counter.textContent = remaining;
        this.tail.textContent = tail || '';
        this.counter.hidden = false;
        this.tail.hidden = false;
        this.detail.textContent = t.cutoff.replace('__TIME__', sameLocalDay ? time : `${date}, ${time}`);
      } else {
        this.lead.textContent = t.next_ship.replace('__DATE__', date);
        this.counter.hidden = true;
        this.tail.hidden = true;
        this.detail.textContent = t.next_cutoff.replace('__TIME__', `${date}, ${time}`);
      }
    }
  }

  if (!customElements.get('valm-product-shipping')) {
    customElements.define('valm-product-shipping', ValmProductShipping);
  }
})();
